import { useCallback, useEffect, useMemo, useRef, useState, type ReactNode } from 'react';
import {
  Alert,
  App as AntApp,
  Button,
  Card,
  Checkbox,
  Col,
  DatePicker,
  Modal,
  Radio,
  Row,
  Segmented,
  Select,
  Skeleton,
  Space,
  Statistic,
  Table,
  Tag,
  Tooltip,
  Typography,
} from 'antd';
import type { ColumnsType } from 'antd/es/table';
import {
  ArrowDownOutlined,
  ArrowUpOutlined,
  BarChartOutlined,
  DeleteOutlined,
  DownloadOutlined,
  EditOutlined,
  InfoCircleOutlined,
  PlusOutlined,
} from '@ant-design/icons';
import dayjs, { type Dayjs } from 'dayjs';
import {
  AUDIT_SOURCES,
  downloadApiUsageCsv,
  downloadAuditExport,
  getAnalyticsCases,
  getAnalyticsDashboard,
  getAnalyticsDispositions,
  getAnalyticsSla,
  getAnalyticsSummary,
  getAnalyticsTrend,
  getAnalyticsUsage,
  getApiUsage,
  resetAnalyticsDashboard,
  saveAnalyticsDashboard,
  type AnalyticsDashboard,
  type AnalyticsQuery,
  type AnalyticsTrend,
  type AuditSource,
  type CredentialUsage,
  type DashboardWidget,
  type SourcesMap,
  type TrendBucketSize,
  type TrendSeries,
  type WidgetSize,
  type WidgetSpec,
} from '@/api/analytics';
import { isNotAvailable, normalizeApiError, type NormalizedApiError } from '@/api/errors';
import { EmptyState } from '@/components/EmptyState';
import { PageHeader } from '@/components/PageHeader';
import { usePageTitle } from '@/components/pageTitle';

const { Text, Paragraph } = Typography;
const { RangePicker } = DatePicker;

/**
 * A metric that may honestly be ABSENT: null renders as an em-dash plus the reason from the
 * sources map — never a fabricated 0 (the FSD §8 sources-honesty rule, same as oversight.ts).
 */
function HonestStat({
  title,
  value,
  reason,
  formatter,
}: {
  title: string;
  value: number | null | undefined;
  reason?: string | null;
  formatter?: (v: number) => string;
}) {
  if (value === null || value === undefined) {
    return (
      <Statistic
        title={
          <span>
            {title}{' '}
            {reason && (
              <Tooltip title={reason}>
                <InfoCircleOutlined style={{ fontSize: 12, color: 'var(--ol-text-tertiary)' }} />
              </Tooltip>
            )}
          </span>
        }
        value="—"
      />
    );
  }
  return <Statistic title={title} value={formatter ? formatter(value) : value} />;
}

/** The `_`-prefixed reason for a null metric, when the API stated one. */
function reasonFor(sources: SourcesMap | undefined, key: string): string | null {
  if (!sources) return null;
  return sources[`_${key}`] ?? sources['_confined'] ?? null;
}

/** A minimal inline bar — deliberately plain antd/CSS; no chart dependency (a real decision). */
function MiniBars({ buckets }: { buckets: { label: string; a: number; b?: number }[] }) {
  const max = Math.max(1, ...buckets.map((x) => Math.max(x.a, x.b ?? 0)));
  return (
    <div style={{ display: 'flex', alignItems: 'flex-end', gap: 4, height: 120, overflowX: 'auto' }}>
      {buckets.map((x) => (
        <Tooltip key={x.label} title={`${x.label}: opened ${x.a}${x.b !== undefined ? `, closed ${x.b}` : ''}`}>
          <div style={{ display: 'flex', gap: 1, alignItems: 'flex-end', minWidth: 10 }}>
            <div
              style={{
                width: 5,
                height: Math.max(2, (x.a / max) * 110),
                background: 'var(--ol-primary)',
                borderRadius: 2,
              }}
            />
            {x.b !== undefined && (
              <div
                style={{
                  width: 5,
                  height: Math.max(2, (x.b / max) * 110),
                  background: 'var(--ol-text-tertiary)',
                  borderRadius: 2,
                }}
              />
            )}
          </div>
        </Tooltip>
      ))}
    </div>
  );
}

/** Group colours for stacked trend bars: fixed order, so a group keeps its colour across buckets. */
const SERIES_COLOURS = [
  '#3b6fd8',
  '#e08a2e',
  '#2e9e6b',
  '#c4485a',
  '#7f5bc4',
  '#2a9bb0',
  '#b0902a',
  '#5c6b7a',
  '#9aa3ad',
];

function StackedBars({ trend }: { trend: AnalyticsTrend }) {
  const groups = trend.groups ?? [];
  const buckets = trend.buckets ?? [];
  const totals = buckets.map((b) => groups.reduce((s, g) => s + (b.counts[g] ?? 0), 0));
  const max = Math.max(1, ...totals);
  return (
    <div>
      <div
        style={{ display: 'flex', alignItems: 'flex-end', gap: 3, height: 120, overflowX: 'auto' }}
        data-testid={`trend-bars-${trend.series}`}
      >
        {buckets.map((b, i) => (
          <Tooltip
            key={b.bucketStart}
            title={
              <div>
                <div>{b.bucketStart.slice(0, 10)}</div>
                {groups
                  .filter((g) => (b.counts[g] ?? 0) > 0)
                  .map((g) => (
                    <div key={g}>
                      {g}: {b.counts[g]}
                    </div>
                  ))}
              </div>
            }
          >
            <div
              style={{
                display: 'flex',
                flexDirection: 'column-reverse',
                width: 10,
                minWidth: 10,
                height: Math.max(2, (totals[i] / max) * 110),
              }}
            >
              {groups.map((g, gi) => {
                const n = b.counts[g] ?? 0;
                if (n === 0 || totals[i] === 0) return null;
                return (
                  <div
                    key={g}
                    style={{
                      flexGrow: n,
                      flexBasis: 0,
                      background: SERIES_COLOURS[gi % SERIES_COLOURS.length],
                    }}
                  />
                );
              })}
            </div>
          </Tooltip>
        ))}
      </div>
      <div style={{ display: 'flex', flexWrap: 'wrap', gap: 4, marginTop: 8 }}>
        {groups.map((g, gi) => (
          <Tag key={g} color={SERIES_COLOURS[gi % SERIES_COLOURS.length]}>
            {g}: {trend.totals?.[g] ?? 0}
          </Tag>
        ))}
      </div>
    </div>
  );
}

/** The sources line under each card — the honesty contract made visible. */
function SourceLine({ sources, metric }: { sources?: SourcesMap; metric: string }) {
  if (!sources) return null;
  const value = sources[metric];
  return (
    <Paragraph type="secondary" style={{ marginTop: 12, marginBottom: 0, fontSize: 11 }}>
      source: {value ?? `none — ${sources[`_${metric}`] ?? 'not wired'}`}
    </Paragraph>
  );
}

// ---- data: one request per (endpoint, query), shared by every widget that needs it -----------------

type Loaded<T> = { data?: T; error?: NormalizedApiError; loading: boolean };

type Fetcher = <T>(key: string, load: () => Promise<T>) => Promise<T>;

function useLoad<T>(fetcher: Fetcher, key: string, load: () => Promise<T>): Loaded<T> {
  const [state, setState] = useState<Loaded<T>>({ loading: true });
  const loadRef = useRef(load);
  loadRef.current = load;
  useEffect(() => {
    let live = true;
    setState((s) => ({ data: s.data, loading: true }));
    fetcher(key, () => loadRef.current())
      .then((data) => live && setState({ data, loading: false }))
      .catch((e) => live && setState({ error: normalizeApiError(e), loading: false }));
    return () => {
      live = false;
    };
  }, [fetcher, key]);
  return state;
}

interface WidgetContext {
  query: AnalyticsQuery;
  bucket: TrendBucketSize;
  fetcher: Fetcher;
}

/** The fallback catalogue for a server that predates the dashboard endpoint: the page as it was. */
const FALLBACK_LAYOUT: DashboardWidget[] = [
  { widget: 'kpi.cases', size: 'M' },
  { widget: 'kpi.runs', size: 'M' },
  { widget: 'sla.summary', size: 'M' },
  { widget: 'dispositions.mix', size: 'M' },
  { widget: 'usage.meter', size: 'L' },
];

const SPAN: Record<WidgetSize, { xs: number; lg: number }> = {
  S: { xs: 24, lg: 8 },
  M: { xs: 24, lg: 12 },
  L: { xs: 24, lg: 24 },
};

const TREND_OF: Record<string, TrendSeries> = {
  'trend.runs-outcome': 'runs.outcome',
  'trend.runs-flow': 'runs.flow',
  'trend.cases-stage': 'cases.stage',
  'trend.cases-sla': 'cases.sla',
  'trend.cases-outcome': 'cases.outcome',
};

/** A refused or failed widget says why, in place; the rest of the dashboard carries on. */
function WidgetProblem({ error, spec }: { error: NormalizedApiError; spec?: WidgetSpec }) {
  if (error.httpStatus === 403) {
    return (
      <Alert
        type="info"
        showIcon
        message="Not available to you"
        description={
          <span>
            This widget reads data that needs <Text code>{spec?.requiredKey ?? 'a permission you do not hold'}</Text>.
            Remove it from your dashboard, or ask a hub admin.
          </span>
        }
      />
    );
  }
  return <Alert type="warning" showIcon message="Could not load" description={error.message} />;
}

function Body<T>({
  loaded,
  spec,
  children,
}: {
  loaded: Loaded<T>;
  spec?: WidgetSpec;
  children: (data: T) => ReactNode;
}) {
  if (loaded.error) return <WidgetProblem error={loaded.error} spec={spec} />;
  if (!loaded.data) return <Skeleton active paragraph={{ rows: 3 }} />;
  return <>{children(loaded.data)}</>;
}

function useSummary(ctx: WidgetContext) {
  return useLoad(ctx.fetcher, `summary`, () => getAnalyticsSummary(ctx.query));
}

function KpiCases({ ctx, spec }: { ctx: WidgetContext; spec?: WidgetSpec }) {
  const summary = useSummary(ctx);
  return (
    <Body loaded={summary} spec={spec}>
      {(s) => (
        <>
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <HonestStat title="Opened" value={s.cases?.opened} />
            </Col>
            <Col xs={12} md={6}>
              <HonestStat title="Closed" value={s.cases?.closed} />
            </Col>
            <Col xs={12} md={6}>
              <HonestStat title="Still open" value={s.cases?.open} />
            </Col>
            <Col xs={12} md={6}>
              <HonestStat title="SLA breached" value={s.cases?.slaBreached} />
            </Col>
          </Row>
          <SourceLine sources={s.sources} metric="cases" />
        </>
      )}
    </Body>
  );
}

function KpiRuns({ ctx, spec }: { ctx: WidgetContext; spec?: WidgetSpec }) {
  const summary = useSummary(ctx);
  return (
    <Body loaded={summary} spec={spec}>
      {(s) => {
        const why = reasonFor(s.sources, 'confined');
        return (
          <>
            <Row gutter={16}>
              <Col xs={12} md={8}>
                <HonestStat title="Runs started" value={s.runs?.started} reason={why} />
              </Col>
              <Col xs={12} md={8}>
                <HonestStat title="Completed" value={s.runs?.completed} reason={why} />
              </Col>
              <Col xs={12} md={8}>
                <HonestStat title="Failed" value={s.runs?.failed} reason={why} />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col xs={12} md={8}>
                <HonestStat title="Alerts open" value={s.alerts?.open} reason={why} />
              </Col>
              <Col xs={12} md={8}>
                <HonestStat title="High severity" value={s.alerts?.high} reason={why} />
              </Col>
              <Col xs={12} md={8}>
                <HonestStat title="Auto-routed" value={s.alerts?.routed} reason={why} />
              </Col>
            </Row>
            <Row gutter={16} style={{ marginTop: 12 }}>
              <Col span={12}>
                <HonestStat
                  title="Repeat-signal rate"
                  value={s.repeatSignalRate}
                  reason={reasonFor(s.sources, 'repeatSignalRate')}
                  formatter={(v) => `${(v * 100).toFixed(1)}%`}
                />
              </Col>
            </Row>
            <SourceLine sources={s.sources} metric="alerts" />
          </>
        );
      }}
    </Body>
  );
}

function CasesVolume({ ctx, spec }: { ctx: WidgetContext; spec?: WidgetSpec }) {
  const bucket = ctx.bucket === 'day' ? 'day' : 'week'; // /cases has no monthly grain
  const cases = useLoad(ctx.fetcher, `cases:${bucket}`, () => getAnalyticsCases({ ...ctx.query, bucket }));
  return (
    <Body loaded={cases} spec={spec}>
      {(c) =>
        c.buckets.length === 0 ? (
          <Text type="secondary">No cases in this window.</Text>
        ) : (
          <>
            <MiniBars
              buckets={c.buckets.map((b) => ({ label: b.bucketStart.slice(0, 10), a: b.opened, b: b.closed }))}
            />
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              opened (accent) vs closed (grey) per {c.bucket}
            </Text>
            <SourceLine sources={c.sources} metric="buckets" />
          </>
        )
      }
    </Body>
  );
}

function TrendWidget({
  ctx,
  spec,
  series,
}: {
  ctx: WidgetContext;
  spec?: WidgetSpec;
  series: TrendSeries;
}) {
  const trend = useLoad(ctx.fetcher, `trend:${series}:${ctx.bucket}`, () =>
    getAnalyticsTrend({ ...ctx.query, series, bucket: ctx.bucket }),
  );
  return (
    <Body loaded={trend} spec={spec}>
      {(t) =>
        t.buckets === null ? (
          <Alert
            type="info"
            showIcon
            message="Withheld"
            description={t.sources[`_${series}`] ?? 'This series has no source for you.'}
          />
        ) : t.buckets.length === 0 ? (
          <>
            <Text type="secondary">Nothing in this window.</Text>
            <SourceLine sources={t.sources} metric={series} />
          </>
        ) : (
          <>
            <StackedBars trend={t} />
            <SourceLine sources={t.sources} metric={series} />
          </>
        )
      }
    </Body>
  );
}

function SlaWidget({ ctx, spec }: { ctx: WidgetContext; spec?: WidgetSpec }) {
  const sla = useLoad(ctx.fetcher, 'sla', () => getAnalyticsSla(ctx.query));
  return (
    <Body loaded={sla} spec={spec}>
      {(l) => (
        <>
          <Row gutter={16}>
            <Col xs={12} md={8}>
              <HonestStat title="Items with SLA" value={l.withSla} />
            </Col>
            <Col xs={12} md={8}>
              <HonestStat title="Breached" value={l.breached} />
            </Col>
            <Col xs={12} md={8}>
              <HonestStat
                title="Breach rate"
                value={l.breachRate}
                reason={reasonFor(l.sources, 'breachRate')}
                formatter={(v) => `${(v * 100).toFixed(1)}%`}
              />
            </Col>
          </Row>
          <div style={{ marginTop: 12, display: 'flex', gap: 8, flexWrap: 'wrap' }}>
            {l.openAgeing.map((b) => (
              <Tag key={b.label}>
                {b.label}: {b.count}
              </Tag>
            ))}
            <Text type="secondary" style={{ fontSize: 11.5 }}>
              open-item ageing
            </Text>
          </div>
          <SourceLine sources={l.sources} metric="sla" />
        </>
      )}
    </Body>
  );
}

const dispositionColumns: ColumnsType<{ action: string; count: number }> = [
  { title: 'Disposition', dataIndex: 'action' },
  { title: 'Count', dataIndex: 'count', width: 100, align: 'right' },
];

function DispositionsWidget({ ctx, spec }: { ctx: WidgetContext; spec?: WidgetSpec }) {
  const mix = useLoad(ctx.fetcher, 'dispositions', () => getAnalyticsDispositions(ctx.query));
  return (
    <Body loaded={mix} spec={spec}>
      {(d) => (
        <>
          {d.mix.length === 0 ? (
            <Text type="secondary">No dispositions in this window.</Text>
          ) : (
            <Table size="small" rowKey="action" columns={dispositionColumns} dataSource={d.mix} pagination={false} />
          )}
          <SourceLine sources={d.sources} metric="mix" />
        </>
      )}
    </Body>
  );
}

function UsageMeterWidget({ ctx, spec }: { ctx: WidgetContext; spec?: WidgetSpec }) {
  const usage = useLoad(ctx.fetcher, 'usage', () => getAnalyticsUsage(ctx.query));
  return (
    <Body loaded={usage} spec={spec}>
      {(u) =>
        u.meter ? (
          <Row gutter={16}>
            <Col xs={12} md={6}>
              <Statistic title="Entities under management" value={u.meter.entitiesUnderManagement.total} />
            </Col>
            <Col xs={12} md={6}>
              <Statistic title="Agent case count" value={u.meter.agentCaseCount.total} />
            </Col>
            <Col span={12}>
              <Text type="secondary" style={{ fontSize: 12 }}>
                Grain {u.meter.meterKey}. The same numbers /v1/metering serves; pricing lives in the
                commercial spine only.
              </Text>
            </Col>
          </Row>
        ) : (
          <Alert
            type="info"
            showIcon
            message="Usage withheld"
            description={
              u.sources?.['_meter'] ?? 'Usage is a cross-spoke oversight read — unavailable for this principal.'
            }
          />
        )
      }
    </Body>
  );
}

const STATUS_COLOURS: Record<string, string> = {
  '2xx': 'green',
  '3xx': 'blue',
  '4xx': 'orange',
  '429': 'volcano',
  '5xx': 'red',
};

function ApiUsageWidget({ ctx, spec }: { ctx: WidgetContext; spec?: WidgetSpec }) {
  const { message } = AntApp.useApp();
  const usageBucket = 'day' as const; // hour grain is for the API; the card shows daily totals
  const usage = useLoad(ctx.fetcher, `api-usage:${usageBucket}`, () =>
    getApiUsage({ ...ctx.query, bucket: usageBucket }),
  );
  const columns: ColumnsType<CredentialUsage> = [
    {
      title: 'Credential',
      key: 'credential',
      render: (_, c) => (
        <div>
          <div>{c.name ?? <Text type="secondary">(no name on record)</Text>}</div>
          <Text type="secondary" style={{ fontSize: 11 }}>
            {c.credentialType === 'API_KEY' ? 'API key' : 'OAuth2 client'} · <Text code>{c.credentialId}</Text>
          </Text>
        </div>
      ),
    },
    {
      title: 'Status',
      dataIndex: 'status',
      width: 100,
      render: (s: string) => <Tag color={s === 'ACTIVE' ? 'green' : 'default'}>{s}</Tag>,
    },
    { title: 'Requests', dataIndex: 'total', width: 100, align: 'right' },
    {
      title: 'By result',
      key: 'classes',
      render: (_, c) => (
        <Space size={2} wrap>
          {Object.entries(c.byStatusClass)
            .filter(([, n]) => n > 0)
            .map(([k, n]) => (
              <Tag key={k} color={STATUS_COLOURS[k]}>
                {k}: {n}
              </Tag>
            ))}
        </Space>
      ),
    },
    {
      title: 'Top routes',
      key: 'routes',
      render: (_, c) => (
        <Text style={{ fontSize: 12 }}>
          {c.byRoute
            .slice(0, 3)
            .map((r) => `${r.routeFamily} ${r.count}`)
            .join(' · ')}
        </Text>
      ),
    },
    {
      title: 'Last seen',
      dataIndex: 'lastSeenAt',
      width: 150,
      render: (v: string) => dayjs(v).format('YYYY-MM-DD HH:mm'),
    },
  ];
  return (
    <Body loaded={usage} spec={spec}>
      {(u) => (
        <>
          <Row gutter={16} align="middle" style={{ marginBottom: 8 }}>
            <Col xs={12} md={8}>
              <Statistic title="Machine requests" value={u.total} />
            </Col>
            <Col xs={12} md={8}>
              <Statistic title="Credentials with traffic" value={u.credentialCount} />
            </Col>
            <Col span={8} style={{ textAlign: 'right' }}>
              <Button
                icon={<DownloadOutlined />}
                data-testid="api-usage-csv"
                onClick={() =>
                  downloadApiUsageCsv(ctx.query).catch((e) => message.error(normalizeApiError(e).message))
                }
              >
                CSV
              </Button>
            </Col>
          </Row>
          {u.credentials.length === 0 ? (
            <Text type="secondary">No machine traffic in this window.</Text>
          ) : (
            <Table
              size="small"
              rowKey={(c) => `${c.credentialType}:${c.credentialId}`}
              columns={columns}
              dataSource={u.credentials}
              pagination={false}
              data-testid="api-usage-table"
            />
          )}
          {u.sources['_credentials'] && (
            <Paragraph type="secondary" style={{ marginTop: 8, marginBottom: 0, fontSize: 11 }}>
              {u.sources['_credentials']}
            </Paragraph>
          )}
          <SourceLine sources={u.sources} metric="usage" />
        </>
      )}
    </Body>
  );
}

function WidgetContent({ id, ctx, spec }: { id: string; ctx: WidgetContext; spec?: WidgetSpec }) {
  if (TREND_OF[id]) return <TrendWidget ctx={ctx} spec={spec} series={TREND_OF[id]} />;
  switch (id) {
    case 'kpi.cases':
      return <KpiCases ctx={ctx} spec={spec} />;
    case 'kpi.runs':
      return <KpiRuns ctx={ctx} spec={spec} />;
    case 'chart.cases-volume':
      return <CasesVolume ctx={ctx} spec={spec} />;
    case 'sla.summary':
      return <SlaWidget ctx={ctx} spec={spec} />;
    case 'dispositions.mix':
      return <DispositionsWidget ctx={ctx} spec={spec} />;
    case 'usage.meter':
      return <UsageMeterWidget ctx={ctx} spec={spec} />;
    case 'api.usage':
      return <ApiUsageWidget ctx={ctx} spec={spec} />;
    default:
      return <Text type="secondary">This console does not know this widget yet.</Text>;
  }
}

const FALLBACK_TITLES: Record<string, string> = {
  'kpi.cases': 'Cases',
  'kpi.runs': 'Runs & alerts',
  'sla.summary': 'SLA',
  'dispositions.mix': 'Disposition mix',
  'usage.meter': 'Usage (metering passthrough — never pricing)',
};

// ---- the audit export ---------------------------------------------------------------------------

const AUDIT_LABELS: Record<AuditSource, string> = {
  case_action: 'Case actions (overrides, redo, re-request, four-eyes)',
  work_item: 'Queue dispositions',
  triage: 'Signal triage decisions',
  flow_approval: 'Flow approvals',
  document_open: 'Document opens',
};

const AUDIT_MAX_DAYS = 93;

function AuditExportModal({
  open,
  onClose,
  query,
}: {
  open: boolean;
  onClose: () => void;
  query: AnalyticsQuery;
}) {
  const { message } = AntApp.useApp();
  const [sources, setSources] = useState<AuditSource[]>([...AUDIT_SOURCES]);
  const [format, setFormat] = useState<'csv' | 'json'>('csv');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<NormalizedApiError | null>(null);
  const days = dayjs(query.to).diff(dayjs(query.from), 'day');
  const tooWide = days > AUDIT_MAX_DAYS;

  const run = () => {
    setBusy(true);
    setError(null);
    downloadAuditExport({ ...query, format, sources })
      .then(() => {
        message.success('Audit trail downloaded');
        onClose();
      })
      .catch((e) => setError(normalizeApiError(e)))
      .finally(() => setBusy(false));
  };

  return (
    <Modal
      title="Export the audit trail"
      open={open}
      onCancel={onClose}
      okText="Download"
      onOk={run}
      okButtonProps={{
        disabled: sources.length === 0 || tooWide,
        loading: busy,
        ...({ 'data-testid': 'audit-export-go' } as Record<string, string>),
      }}
      destroyOnClose
    >
      <Paragraph type="secondary">
        {dayjs(query.from).format('YYYY-MM-DD')} to {dayjs(query.to).subtract(1, 'day').format('YYYY-MM-DD')} (the
        page&apos;s range, at most {AUDIT_MAX_DAYS} days). Who did what, when: one line per act. Erased subjects show
        as Erased; Aadhaar numbers keep their last four digits only. Evidence, documents and payloads are not
        included.
      </Paragraph>
      {tooWide && (
        <Alert
          type="warning"
          showIcon
          style={{ marginBottom: 12 }}
          message={`The range is ${days} days. Narrow it to ${AUDIT_MAX_DAYS} days or fewer to export.`}
        />
      )}
      <Checkbox.Group
        value={sources}
        onChange={(v) => setSources(v as AuditSource[])}
        style={{ display: 'flex', flexDirection: 'column', gap: 4, marginBottom: 12 }}
        options={AUDIT_SOURCES.map((s) => ({ label: AUDIT_LABELS[s], value: s }))}
      />
      <Radio.Group value={format} onChange={(e) => setFormat(e.target.value)}>
        <Radio value="csv">CSV (spreadsheet)</Radio>
        <Radio value="json">JSON</Radio>
      </Radio.Group>
      {error && (
        <Alert
          type="error"
          showIcon
          style={{ marginTop: 12 }}
          message={error.httpStatus === 403 ? 'Not allowed' : 'Export refused'}
          description={
            error.httpStatus === 403 && error.code !== 'AUDIT_EXPORT_CONFINED' ? (
              <span>
                Exporting the audit trail needs <Text code>audit.export</Text> (hub admin or auditor).
              </span>
            ) : (
              error.message
            )
          }
        />
      )}
    </Modal>
  );
}

// ---- the page -----------------------------------------------------------------------------------

export function AnalyticsPage() {
  usePageTitle('Analytics');
  const { message } = AntApp.useApp();
  const [range, setRange] = useState<[Dayjs, Dayjs]>([dayjs().subtract(30, 'day'), dayjs()]);
  const [bucket, setBucket] = useState<TrendBucketSize>('day');
  const [dashboard, setDashboard] = useState<AnalyticsDashboard | null>(null);
  const [dashboardMissing, setDashboardMissing] = useState(false);
  const [unavailable, setUnavailable] = useState(false);
  const [editing, setEditing] = useState(false);
  const [draft, setDraft] = useState<DashboardWidget[]>([]);
  const [adding, setAdding] = useState<string | undefined>();
  const [saving, setSaving] = useState(false);
  const [exportOpen, setExportOpen] = useState(false);
  const [refreshTick, setRefreshTick] = useState(0);

  const query = useMemo(
    () => ({
      // Half-open [from, to): 'to' becomes next-day 00:00Z (the MeteringPanel convention).
      from: range[0].startOf('day').toISOString(),
      to: range[1].add(1, 'day').startOf('day').toISOString(),
    }),
    [range],
  );

  // One promise per (endpoint, query) for this query: widgets sharing a source share a request.
  const fetcher = useMemo<Fetcher>(() => {
    const cache = new Map<string, Promise<unknown>>();
    return <T,>(key: string, load: () => Promise<T>) => {
      if (!cache.has(key)) cache.set(key, load());
      return cache.get(key) as Promise<T>;
    };
    // refreshTick: a retry builds a fresh cache
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [query, refreshTick]);

  const ctx: WidgetContext = useMemo(() => ({ query, bucket, fetcher }), [query, bucket, fetcher]);

  const loadDashboard = useCallback(() => {
    getAnalyticsDashboard()
      .then((d) => {
        setDashboard(d);
        setDashboardMissing(false);
      })
      .catch((e) => {
        const err = normalizeApiError(e);
        if (isNotAvailable(err)) {
          // An older server: the fixed page, no editing. Its other reads decide the rest.
          setDashboardMissing(true);
          getAnalyticsSummary(query).catch((x) => {
            if (isNotAvailable(normalizeApiError(x))) setUnavailable(true);
          });
        } else {
          setDashboardMissing(true);
          message.error(`Your saved dashboard could not be loaded: ${err.message}`);
        }
      });
  }, [message, query]);

  useEffect(() => {
    loadDashboard();
    // the dashboard does not depend on the range
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const catalogue = dashboard?.catalogue ?? [];
  const specOf = (id: string) => catalogue.find((w) => w.id === id);
  const layout = editing ? draft : (dashboard?.layout ?? (dashboardMissing ? FALLBACK_LAYOUT : []));

  const startEdit = () => {
    setDraft(dashboard?.layout ?? []);
    setEditing(true);
  };

  const move = (i: number, by: number) => {
    setDraft((d) => {
      const next = [...d];
      const j = i + by;
      if (j < 0 || j >= next.length) return d;
      [next[i], next[j]] = [next[j], next[i]];
      return next;
    });
  };

  const save = () => {
    if (!dashboard) return;
    setSaving(true);
    saveAnalyticsDashboard(draft, dashboard.version)
      .then((d) => {
        setDashboard(d);
        setEditing(false);
        message.success('Dashboard saved');
      })
      .catch((e) => {
        const err = normalizeApiError(e);
        if (err.httpStatus === 403) {
          message.error('Saving a layout needs analytics.dashboard.write (hub admin, hub operator or analyst).');
        } else if (err.code === 'DASHBOARD_VERSION_CONFLICT') {
          message.warning('Your dashboard changed in another tab. Reloaded it: apply your change again.');
          loadDashboard();
          setEditing(false);
        } else {
          message.error(err.message);
        }
      })
      .finally(() => setSaving(false));
  };

  const reset = () => {
    setSaving(true);
    resetAnalyticsDashboard()
      .then((d) => {
        setDashboard(d);
        setEditing(false);
        message.success('Back to the default dashboard');
      })
      .catch((e) => {
        const err = normalizeApiError(e);
        message.error(
          err.httpStatus === 403
            ? 'Resetting a layout needs analytics.dashboard.write (hub admin, hub operator or analyst).'
            : err.message,
        );
      })
      .finally(() => setSaving(false));
  };

  if (unavailable) {
    return (
      <EmptyState
        icon={<BarChartOutlined />}
        title="Analytics"
        description="Per-hub operational analytics over the cases, runs, alerts and usage this deployment already records. This deployment does not expose the analytics API yet."
        bullets={[
          'Case volume, SLA breach rate and ageing, disposition mix — over the same rows your queue shows.',
          'Usage passthrough of the metering grain (never pricing — that lives in commercials).',
          'Honest by construction: a metric with no source reads as —, never a fabricated zero.',
        ]}
      />
    );
  }

  const full = (dashboard?.maxWidgets ?? 24) <= draft.length;

  return (
    <div className="ol-page">
      <PageHeader
        title="Analytics"
        icon={<BarChartOutlined />}
        subtitle={
          <>
            Read-only analytics over this hub&apos;s existing records, on a dashboard you arrange. Metrics with
            no real source show &mdash; with the reason — never a plausible zero. Confined analysts see numbers
            that match their own queue.
          </>
        }
      />
      <Row gutter={[12, 12]} align="middle" style={{ marginBottom: 8 }}>
        <Col>
          {!editing ? (
            <Space wrap>
              <Button
                icon={<EditOutlined />}
                onClick={startEdit}
                disabled={!dashboard}
                data-testid="dashboard-customize"
              >
                Customize
              </Button>
              <Button icon={<DownloadOutlined />} onClick={() => setExportOpen(true)} data-testid="audit-export-open">
                Export audit trail
              </Button>
              {dashboard && !dashboard.isDefault && <Tag>Your layout</Tag>}
            </Space>
          ) : (
            <Space wrap>
              <Select
                placeholder="Add a widget"
                style={{ width: 260 }}
                showSearch
                optionFilterProp="label"
                virtual={false}
                listHeight={420}
                value={adding}
                onChange={setAdding}
                disabled={full}
                data-testid="dashboard-add-select"
                options={catalogue.map((w) => ({ value: w.id, label: `${w.title} (${w.category})` }))}
              />
              <Button
                icon={<PlusOutlined />}
                disabled={!adding || full}
                data-testid="dashboard-add"
                onClick={() => {
                  const spec = specOf(adding!);
                  if (spec) setDraft((d) => [...d, { widget: spec.id, size: spec.defaultSize }]);
                  setAdding(undefined);
                }}
              >
                Add
              </Button>
              <Button type="primary" loading={saving} onClick={save} data-testid="dashboard-save">
                Save layout
              </Button>
              <Button onClick={() => setEditing(false)} disabled={saving}>
                Cancel
              </Button>
              <Button danger onClick={reset} disabled={saving} data-testid="dashboard-reset">
                Reset to default
              </Button>
            </Space>
          )}
        </Col>
        <Col flex="auto" />
        <Col>
          <RangePicker
            value={range}
            allowClear={false}
            onChange={(v) => {
              if (v && v[0] && v[1]) setRange([v[0], v[1]]);
            }}
          />
        </Col>
        <Col>
          <Segmented
            options={[
              { label: 'Daily', value: 'day' },
              { label: 'Weekly', value: 'week' },
              { label: 'Monthly', value: 'month' },
            ]}
            value={bucket}
            onChange={(v) => setBucket(v as TrendBucketSize)}
          />
        </Col>
      </Row>

      {!dashboard && !dashboardMissing ? (
        <Skeleton active paragraph={{ rows: 8 }} />
      ) : layout.length === 0 ? (
        <EmptyState
          icon={<BarChartOutlined />}
          title="An empty dashboard"
          description="Choose Customize and add widgets, or reset to the default."
        />
      ) : (
        <Row gutter={[16, 16]}>
          {layout.map((w, i) => {
            const spec = specOf(w.widget);
            const title = spec?.title ?? FALLBACK_TITLES[w.widget] ?? w.widget;
            return (
              <Col key={`${w.widget}-${i}`} {...SPAN[w.size]} data-testid={`widget-${w.widget}`}>
                <Card
                  size="small"
                  title={
                    spec ? (
                      <Tooltip title={spec.description}>
                        <span>{title}</span>
                      </Tooltip>
                    ) : (
                      title
                    )
                  }
                  extra={
                    editing && (
                      <Space size={4}>
                        <Select
                          size="small"
                          value={w.size}
                          style={{ width: 64 }}
                          aria-label="Widget size"
                          options={(spec?.sizes ?? ['S', 'M', 'L']).map((s) => ({ value: s, label: s }))}
                          onChange={(size) =>
                            setDraft((d) => d.map((x, j) => (j === i ? { ...x, size: size as WidgetSize } : x)))
                          }
                        />
                        <Button
                          size="small"
                          icon={<ArrowUpOutlined />}
                          aria-label="Move up"
                          disabled={i === 0}
                          onClick={() => move(i, -1)}
                        />
                        <Button
                          size="small"
                          icon={<ArrowDownOutlined />}
                          aria-label="Move down"
                          disabled={i === layout.length - 1}
                          onClick={() => move(i, 1)}
                        />
                        <Button
                          size="small"
                          danger
                          icon={<DeleteOutlined />}
                          aria-label="Remove widget"
                          data-testid={`remove-${w.widget}`}
                          onClick={() => setDraft((d) => d.filter((_, j) => j !== i))}
                        />
                      </Space>
                    )
                  }
                >
                  {editing ? (
                    <Text type="secondary">{spec?.description}</Text>
                  ) : (
                    <WidgetContent id={w.widget} ctx={ctx} spec={spec} />
                  )}
                </Card>
              </Col>
            );
          })}
        </Row>
      )}
      {!editing && (
        <div style={{ marginTop: 12 }}>
          <Button size="small" type="link" onClick={() => setRefreshTick((t) => t + 1)}>
            Refresh
          </Button>
        </div>
      )}
      <AuditExportModal open={exportOpen} onClose={() => setExportOpen(false)} query={query} />
    </div>
  );
}
